<template>
  <div class="flex flex-wrap justify-center relative pb-16 md:pb-24 overflow-x-hidden">
    <img src="~/assets/img/shapes/circle-drk-red-2.svg" class="circles--right">
    <img src="~/assets/img/shapes/circle-light-red.svg" class="circles--left">

    <header class="w-3/4 md:w-2/3 lg:w-1/3 flex flex-col pt-24 pb-10 text-center items-center">
      <div class="h2 text-secondary mb-2">
        Registreren
      </div>
      <div class="prose content" v-html="page.template.introductie"/>
    </header>

    <section class="wave-shape pt-4 md:pt-12">
      <div class="container flex flex-wrap justify-center">
        <div class="w-full flex justify-center">
          <select-box v-model="selectedType" :options="registrationTypes" required label="Registreren als"/>
        </div>

        <form class="subscription-form" @submit.prevent="submit">
          <section v-if="selectedType === PRAKTIJK" class="flex flex-col col-span-2 md:col-span-1">
            <header class="h2 text-secondary mb-4">
              Praktijk
            </header>
            <text-field id="parktijknaam" v-model="form.company_name" required placeholder="Praktijknaam" :error-messages="errors['company_name']">
              Praktijknaam
            </text-field>
            <text-field id="btw-nummer" v-model="form.tax_number" required placeholder="BTW-nummer" :error-messages="errors['tax_number']">
              BTW-nummer
            </text-field>
            <text-field id="kvk-nummer" v-model="form.kvk_number" required placeholder="KVK-nummer" :error-messages="errors['kvk_number']">
              KVK-nummer
            </text-field>
          </section>

          <section class="flex flex-col col-span-2 md:col-span-1">
            <header class="h2 text-secondary mb-4">
              Persoonsgegevens
            </header>
            <text-field id="first-name" v-model="form.first_name" required placeholder="Voornaam" :error-messages="errors['first_name']">
              Voornaam
            </text-field>
            <text-field id="last-name" v-model="form.last_name" required placeholder="Achternaam" :error-messages="errors['last_name']">
              Achternaam
            </text-field>
            <text-field id="email" v-model="form.email" required type="email" placeholder="E-mailadres" :error-messages="errors['email']">
              E-mailadres
            </text-field>
            <text-field id="tel" v-model="form.phone" required placeholder="Telefoonnummer" :error-messages="errors['phone']">
              Telefoonnummer
            </text-field>
          </section>

          <section class="flex flex-col col-span-2 md:col-span-1">
            <header class="h2 text-secondary mb-4">
              Adres
            </header>
            <text-field id="street" v-model="form.street" required placeholder="Straatnaam" :error-messages="errors['street']">
              Straatnaam
            </text-field>
            <div class="flex">
              <text-field
                id="housenumber"
                v-model="form.street_number"
                type="number"
                required
                placeholder="Huisnum."
                class="w-1/2 pr-2"
                :error-messages="errors['street_number']"
              >
                Huisnummer
              </text-field>
              <text-field id="housenumber-add" v-model="form.street_number_addition" placeholder="Toevoeg." class="w-1/2 pl-2" :error-messages="errors['street_number_addition']">
                Toevoeging
              </text-field>
            </div>
            <text-field id="postal" v-model="form.postal_code" required placeholder="Postcode" :error-messages="errors['postal_code']">
              Postcode
            </text-field>
            <text-field id="city" v-model="form.city" required placeholder="Stad" :error-messages="errors['city']">
              Stad
            </text-field>
            <text-field id="country" v-model="form.country" required placeholder="Land" :error-messages="errors['country']">
              Land
            </text-field>
          </section>

          <section class="col-span-2 subscription-form--permissions">
            <header class="h2 text-secondary mb-4">
              Toestemmingen
            </header>
            <checkbox-field v-model="validation.agreement">
              Ja, ik ga akkoord met
              <nuxt-link to="/overeenkomst" class="link-secondary" target="_blank">de overeenkomst</nuxt-link> &
              <nuxt-link to="/verwerkersovereenkomst" class="link-secondary" target="_blank">de verwerkersovereenkomst</nuxt-link>
            </checkbox-field>
            <checkbox-field v-model="validation.general_conditions">
              Ja, ik heb <nuxt-link to="/algemene-voorwaarden" class="link-secondary" target="_blank">de algemene voorwaarden</nuxt-link> gelezen en ga akkoord
            </checkbox-field>
            <checkbox-field v-model="validation.privacy">
              Ja, ik heb <nuxt-link to="/privacy-statement" class="link-secondary" target="_blank">de privacyverklaring</nuxt-link> gelezen en ga akkoord
            </checkbox-field>
            <header class="h2 text-secondary mt-6 xl:mt-12 mb-4">
              Nieuwsbrief
            </header>
            <checkbox-field v-model="form.newsletter">
              Ik wil me inschrijven voor de nieuwsbrief en daarmee op de hoogte blijven van alles rondom Mijn Superkracht!
            </checkbox-field>
          </section>

          <div class="subscription-form--button">
            <button type="submit" class="btn btn-secondary" :disabled="isDisabled">
              Registreer
            </button>
          </div>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import TextField from '~/components/forms/TextField';
import SelectBox from '~/components/forms/SelectBox';
import CheckboxField from '~/components/forms/CheckboxField';
import page from '~/plugins/mixins/page';
import {BEGELEIDER, PRAKTIJK, REGISTRATION_TYPES} from '~/plugins/constants';
import createBegeleider from '~/graphql/mutations/createBegeleider';

export default {
  components: {SelectBox, TextField, CheckboxField},
  mixins: [page],
  data: () => ({
    selectedType: BEGELEIDER,
    registrationTypes: REGISTRATION_TYPES,
    PRAKTIJK,
    form: {
      company_name: null,
      tax_number: null,
      kvk_number: null,
      first_name: null,
      last_name: null,
      email: null,
      phone: null,
      street: null,
      street_number: null,
      street_number_addition: null,
      postal_code: null,
      city: null,
      newsletter: null,
    },
    validation: {
      privacy: false,
      general_conditions: false,
      agreement: false,
    },
  }),
  computed: {
    isDisabled() {
      return !this.validation.privacy || !this.validation.general_conditions || !this.validation.agreement;
    },
  },
  methods: {
    async submit() {
      this.form.register_as_company = this.selectedType === PRAKTIJK;
      try {
        await this.$apollo.mutate({
          mutation: createBegeleider,
          variables: this.form,
        });
        this.$toast.success('Uw aanmelding wordt in behandeling genomen.');
        this.form = {};
        this.validation = {};
      } catch (e) {
        console.log(e);
        this.$toast.error('Er is iets mis gegaan tijdens het aanmaken van de aanmelding. Probeer het later nog eens.');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wave-shape {
  @apply w-full;
  background-image: url("~assets/img/shapes/lightwave.svg");
  background-repeat: no-repeat;
  min-height: 600px;
}

.circles {
  &--right {
    @apply absolute opacity-25;
    top: -22%;
    right: 0;
  }
  &--left {
    @apply absolute;
    top: 35%;
    left: -10%;
  }
}

.subscription-form {
  @apply relative z-20 w-full grid grid-cols-1 gap-4 mt-12;

  @screen md {
    @apply w-2/3 grid-cols-2;
  }
  @screen xl {
    @apply grid-cols-4;
  }
  &--permissions {
    @apply mt-0;
    @screen xl {
      @apply mt-16;
    }
  }
  &--button {
    @apply col-span-1 flex justify-center mt-10;
    @screen md {
      @apply col-span-2;
    }
    @screen xl {
      @apply col-span-4;
    }
  }
}
</style>
